import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import Sound from 'react-sound';
import styled from 'styled-components';

import Time from 'components/shared/Time';
import { Avatar } from '@companycam/slab-web';
import { ActionLink } from 'components/shared';
import { useAvatarData } from 'javascripts/hooks';

// #region styled-components
const VoiceNoteContainer = styled.div`
  display: flex;
  height: 24px;
  width: 100%;
  flex: 1;
  align-items: center;
`;

const VoiceNoteController = styled(ActionLink)`
  position: relative;
  top: -1px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  font-size: 24px;
  color: ${({ isCurrentUser }) =>
    isCurrentUser
      ? 'var(--cc_color_button_text_primary)'
      : 'var(--cc_color_link_primary'};
`;

const VoiceNoteTime = styled.span`
  display: flex;
  width: 50px;
  align-items: center;
  justify-content: center;
  color: ${(props) =>
    props.isCurrentUser
      ? 'var(--cc_color_button_text_primary)'
      : 'var(--cc_color_text_nonessential)'};
`;
// #endregion

const VoiceNote = ({ voiceNote, isCurrentUser }) => {
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const avatarData = useAvatarData(voiceNote);
  const currentCompanyId = useSelector((state) => state.company.node_id);

  useEffect(() => {
    if (!playing) {
      window.soundManager.pauseAll();
    }
  }, [playing]);

  const { contributor } = voiceNote;
  const isDifferentCompany = currentCompanyId !== contributor.companyId;

  let creatorDisplayName = contributor.name || 'Unknown User';
  if (isDifferentCompany && contributor.companyName) {
    creatorDisplayName = `${creatorDisplayName} (${contributor.companyName})`;
  }

  const time = Math.floor(position / 1000);
  const durationSeconds = parseFloat(voiceNote.durationSeconds);
  const fullTime = Math.floor(durationSeconds);
  const timeRemaining = Math.floor(fullTime - time);

  const activityContainer = cx('single-comment', {
    'is-current-user': isCurrentUser,
  });
  const sliderContainer = cx('voice-note-slider', {
    'is-current-user': isCurrentUser,
  });

  return (
    <div className={activityContainer}>
      {avatarData.isExternal ? (
        <Avatar
          fullName={creatorDisplayName}
          imageSrc={avatarData.src}
          alt={creatorDisplayName}
          initials={avatarData.initials}
          borderColor={avatarData.color}
        />
      ) : (
        <Avatar
          fullName={creatorDisplayName}
          imageSrc={avatarData.src}
          alt={creatorDisplayName}
          initials={avatarData.initials}
        />
      )}
      <div className="comment-details">
        <div className="commentor-name">{creatorDisplayName}</div>
        <div className="commentor-comment">
          <VoiceNoteContainer>
            <Sound
              url={voiceNote.url}
              playStatus={playing ? Sound.status.PLAYING : Sound.status.PAUSED}
              position={position}
              onPlaying={(data) => {
                setPosition(data.position);
              }}
              onFinishedPlaying={() => {
                setPlaying(false);
              }}
            />
            <VoiceNoteController
              isCurrentUser={isCurrentUser}
              onClick={() => {
                setPlaying((currentPlaying) => !currentPlaying);
              }}
            >
              <i className={playing ? 'mdi mdi-pause' : 'mdi mdi-play'} />
            </VoiceNoteController>
            <div className={sliderContainer}>
              <input
                type="range"
                min={0}
                max={durationSeconds}
                value={Math.round(position / 1000)}
                onChange={(e) => {
                  setPosition(e.target.value * 1000);
                }}
              />
            </div>
            <VoiceNoteTime isCurrentUser={isCurrentUser}>
              {timeRemaining}s
            </VoiceNoteTime>
          </VoiceNoteContainer>
        </div>
        <div className="comment-date-time">
          <Time date={voiceNote.createdAt} format="Pp" />
        </div>
      </div>
    </div>
  );
};

VoiceNote.propTypes = {
  isCurrentUser: PropTypes.bool,
  voiceNote: PropTypes.object, // VoiceNoteFragment
};

export default VoiceNote;
